//
// AssetMetadataField
//
// An asset-input (image).

import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { DragDropLinkedAsset } from '../../dragdrop/DragDropLinkedAsset'
import { useMousePopover } from '../../hooks/useMousePopover'
import { useTooltipPopover } from '../../hooks/useTooltipPopover'
import { useStore } from '../../stores'

import { AssetFieldMenu, UploadedAssetMenu } from '../../menus'
import { AssetItem, IconTile, UploadErrorMessage } from '../../panels'

export const AssetMetadataField = observer(function AssetMetadataField(props) {
    const {
        className,
        field,
        fieldname,
        fieldpath,
        item,
        language,
        renderkey,
        onChange,
        ...other
    } = props

    const store = useStore()
    const { data, view } = store

    const AssetFieldContextMenu = useMousePopover(AssetFieldMenu)
    const UploadedAssetPopoverMenu = useTooltipPopover(UploadedAssetMenu)
    const [droppedfile, setDroppedFile] = useState(null) // only drop on existing asset

    let classes = 'cc-Field cc-AssetField cc-AssetMetadataField ws-tile'
    if (className) classes += ' ' + className

    const fieldvalue = field.is_localized ? field.value[language] : field.value

    classes += ' cc-AssetField-item'

    const [asset, setAsset] = useState(null)
    useEffect(() => {
        let isMounted = true
        let controller = new AbortController()
        async function asyncEffect() {
            setAsset(null)
            if (fieldvalue) {
                if (!data.assets.has(fieldvalue)) {
                    await data.fetchAssetByGid(fieldvalue, {
                        signal: controller.signal,
                    })
                }
                const asyncasset = data.assets.get(fieldvalue)
                if (isMounted) {
                    setAsset(asyncasset)
                }
            }
        }
        asyncEffect()
        return () => {
            controller.abort('Unmounted')
            isMounted = false
        }
    }, [fieldvalue, data])

    if (asset) {
        classes += ' status-' + asset.status
    }

    const onAddAsset = (asset, otherassetgid) => {
        const metadatafield = item.metadata.get(fieldpath)
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            otherassetgid,
            language
        )
        onChange && onChange(item)
    }

    const onReplaceAsset = (asset, otherassetgid) => {
        const metadatafield = item.metadata.get(fieldpath)
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            otherassetgid,
            language
        )
        onChange && onChange(item)
    }

    const onUploadNewAsset = (asset, file) => {
        setDroppedFile(null)
        const folder_gid = field.options.get('folder')
        if (!file.type) {
            console.log(file.name + ' - folder upload not supported')
            return false
        }
        if (folder_gid) {
            return data.actions.assets
                .upload(file, folder_gid)
                .then(result => {
                    const metadatafield = item.metadata.get(fieldpath)
                    item.setMetadataField(
                        fieldpath,
                        metadatafield.type,
                        metadatafield.is_localized,
                        result['asset'],
                        language
                    )
                    onChange && onChange(item)

                    return result['asset']
                })
                .catch(error => {
                    store.setMessage(<UploadErrorMessage />)
                })
        } else {
            return data.actions.assets
                .uploadToFolder(file, '/')
                .then(result => {
                    const metadatafield = item.metadata.get(fieldpath)
                    item.setMetadataField(
                        fieldpath,
                        metadatafield.type,
                        metadatafield.is_localized,
                        result['asset'],
                        language
                    )
                    onChange && onChange(item)

                    return result['asset']
                })
                .catch(error => {
                    store.setMessage(<UploadErrorMessage />)
                })
        }
    }

    const onUploadUpdateAsset = (asset, file) => {
        setDroppedFile(null)
        if (!file.type) {
            console.log(file.name + ' - folder upload not supported')
            return false
        }
        return data.actions.assets
            .uploadUpdate(file, asset.gid)
            .then(result => {
                // record.setField(field, result['asset'])
                // record.commitIfModified()
                return result['asset']
            })
            .catch(error => {
                store.setMessage(<UploadErrorMessage />)
            })
    }

    const onCancel = () => {
        setDroppedFile(null)
    }

    const onRemoveAsset = () => {
        const metadatafield = item.metadata.get(fieldpath)
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            null,
            language
        )
        onChange && onChange(item)
    }

    const onRevealInDam = asset => {
        view.damworksheet.setAsset(asset)
        view.damtree.datamanager.setAsset(asset)
        // this is a hack to get out of a not-that-well-designed auto-updating
        // system - when lazyvscrolltiles does two refetches when it gets into focus,
        // it fetches the old asset, triggering a new setAsset call... if we wait a tiny
        // bit, it can refetch the newly set asset
        window.setTimeout(() => view.setWorkspace('dam'), 200)
    }

    const onAssetFieldAction = (action, asset, p1) => {
        const actions = {
            add_asset: onAddAsset,
            replace_asset: onReplaceAsset,
            upload_new: onUploadNewAsset,
            upload_update: onUploadUpdateAsset,
            cancel: onCancel,
            remove_asset: onRemoveAsset,
            reveal_in_dam: onRevealInDam,
        }
        AssetFieldContextMenu.hide()
        UploadedAssetPopoverMenu.hide()
        if (!(action in actions)) {
            console.log(`onAssetFieldAction: unhandled action '${action}'`)
            return
        }
        actions[action](asset, p1)
    }

    const onShowAssetFieldContextMenu = e => {
        UploadedAssetPopoverMenu.hide()
        AssetFieldContextMenu.onShow(e)
    }

    const onClickShowAssetFieldContextMenu = e => {
        onShowAssetFieldContextMenu(e)
    }

    const icontileanchor = 'start'
    const iconplaceholder = 'plus'

    const onDrop = (dragitem, linkedasset, isoverzone) => {
        if (dragitem.hasOwnProperty('files')) {
            const file = dragitem.files[0]
            // drop on add
            if (!linkedasset) {
                onUploadNewAsset(linkedasset, file)
            } else {
                // store dropped file, show UploadedAssetMenu first
                setDroppedFile(file)
                UploadedAssetPopoverMenu.show()
            }
        } else {
            // onDrop only 'inside'
            const metadatafield = item.metadata.get(fieldpath)
            item.setMetadataField(
                fieldpath,
                metadatafield.type,
                metadatafield.is_localized,
                dragitem.gid,
                language
            )
            onChange && onChange(item)
        }
    }

    const onUploadAction = (action, p1) => {
        const actions = {
            upload_new: onUploadNewAsset,
            upload_update: onUploadUpdateAsset,
            cancel: onCancel,
        }
        AssetFieldContextMenu.hide()
        UploadedAssetPopoverMenu.hide()
        if (!(action in actions)) {
            console.log(`onUploadAction: unhandled action '${action}'`)
            return
        }
        actions[action](asset, p1)
    }

    return (
        <div className={classes} {...other}>
            <AssetFieldContextMenu.Panel
                asset={asset}
                enabled={true}
                list={false}
                onAction={onAssetFieldAction}
            />
            <UploadedAssetPopoverMenu.Panel
                file={droppedfile}
                onAction={onUploadAction}
            />
            <div ref={UploadedAssetPopoverMenu.anchorRef}>
                <DragDropLinkedAsset field={field} linkedasset={asset} onDrop={onDrop}>
                    {!asset ? (
                        <IconTile
                            width={32}
                            className="cc-dimmed"
                            icon={iconplaceholder}
                            anchor={icontileanchor}
                            onClick={onClickShowAssetFieldContextMenu}
                        />
                    ) : (
                        <AssetItem
                            asset={asset}
                            iconplaceholder={iconplaceholder}
                            onClick={onClickShowAssetFieldContextMenu}
                            onContextMenu={onShowAssetFieldContextMenu}
                        />
                    )}
                </DragDropLinkedAsset>
            </div>
        </div>
    )
})
